import * as React from 'react'
import Tile from '@src/components/common/tile'
import cx from 'classnames'
import type { FinalState } from 'react-table-6'
import * as styles from './user.activity.styl'
import DataGrid, { ColumnType, ColumnProps } from '@src/components/data.grid'
import { CloudTrailLogDetails, UserActivityFilter } from './components'
import { sortedByTimestamp } from './configurations/sorting.configurations'
import type { History } from 'history'
import type { ResourceTypeDict } from '@src/redux/reducers/shell'
import type { TableOptions, UsageItem } from '@src/redux/reducers/user.usage'
import * as Selectors from './selectors'
import { isEqual } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import * as Actions from '@src/redux/actions/userUsage.actions'
import { useEffectUpdate } from '@src/hooks/use.effect.update.hook'

interface OwnProps {
  hideEmpty?: boolean
  columns?: ColumnProps<UsageItem>[]
  query?: string
  id?: string
  userName?: string
  accountId?: string
  history?: History
  resourceTypes?: ResourceTypeDict
  useSearch?: boolean
  useFilter?: boolean
  showToastMessages?: boolean
}
export type Props = OwnProps

const UserActivityAlert: React.FC<Props> = React.memo((props: Props) => {
  const {
    id,
    userName,
    accountId,
    query,
    showToastMessages,
    resourceTypes,
    useSearch,
    useFilter,
    columns
  } = props
  const dispatch = useDispatch()
  const { searchValue, pageNumber, pageSize, sortField, asc } = useSelector(
    Selectors.getTableOptions
  )
  const usageInfoList = useSelector(Selectors.getUsageInfoList)
  const isPending = useSelector(Selectors.getIsPending)
  const startTime = useSelector(Selectors.getStartTime)
  const endTime = useSelector(Selectors.getEndTime)
  const logDetails = useSelector(Selectors.getLogDetails)

  const filterColumns = useSelector(Selectors.getFilterColumnsSelector)
  const selectedFilterColumn = useSelector(Selectors.getSearchField)

  React.useEffect(() => {
    dispatch(
      Actions.fetchDataByGrid(
        {
          query,
          userName,
          accountId,
          pageNumber,
          pageSize,
          sortField,
          asc
        },
        showToastMessages
      )
    )
    return () => {
      dispatch(Actions.cleanerStat('tableOptions'))
    }
  }, [])

  useEffectUpdate(() => {
    dispatch(
      Actions.fetchData(
        {
          query,
          userName,
          accountId
        },
        showToastMessages
      )
    )
  }, [id, query])

  function onFetchDataByFilter({
    startTime,
    endTime
  }: {
    startTime: Date
    endTime: Date
  }) {
    dispatch(
      Actions.fetchByDateRange(
        {
          query,
          userName,
          accountId,
          startTime,
          endTime
        },
        showToastMessages
      )
    )
  }

  function onNextPage(state: FinalState<UsageItem>) {
    onFetchDataByGrid({ ...state, page: state.page + 1 })
  }
  function onPrevPage(state: FinalState<UsageItem>) {
    onFetchDataByGrid({ ...state, page: state.page - 1 })
  }

  function onFetchDataByGrid(state: {
    page: number
    pageSize: number
    sorted: { id?: string; desc?: any }[]
  }) {
    const prevOptions: TableOptions = {
      pageNumber,
      pageSize,
      sortField,
      asc
    }
    const currOptions: TableOptions = {
      pageNumber: state.page,
      pageSize: state.pageSize,
      sortField: state.sorted.length > 0 ? state.sorted[0].id : '',
      asc: state.sorted.length > 0 ? !state.sorted[0].desc : true
    }

    if (!isEqual(prevOptions, currOptions)) {
      dispatch(
        Actions.fetchDataByGrid(
          {
            query,
            userName,
            accountId,
            pageNumber: state.page,
            pageSize: state.pageSize,
            sortField: state.sorted.length > 0 ? state.sorted[0].id : '',
            asc: state.sorted.length > 0 ? !state.sorted[0].desc : true
          },
          showToastMessages
        )
      )
    }
  }

  function onCloseLogDetails() {
    dispatch(Actions.clearCTLogDetails())
  }

  function onShowLogDetails(item: { event_id: any }) {
    const { event_id } = item
    if (event_id) {
      dispatch(Actions.getCTLogDetails(event_id))
    }
  }

  const lastPage = usageInfoList?.length < pageSize
  const canNext = !lastPage || usageInfoList?.length >= pageSize
  const gridColumns = [
    {
      title: '',
      descriptionCell: 'More Info',
      type: ColumnType.button,
      clickable: data => data.event_id,
      template: data => {
        const { original } = data
        const { event_id } = original
        return event_id ? (
          <i className={cx('material-icons', styles.iconLogDetails)}>
            more info
          </i>
        ) : null
      },
      onClick: (item: any) => onShowLogDetails(item || {})
    },
    ...columns
  ]

  return (
    <>
      <Tile title="User Activity" className={styles.userActivityAlert}>
        {useFilter && (
          <UserActivityFilter
            showToastMessages={showToastMessages}
            useSearch={useSearch}
            query={query}
            searchValue={searchValue}
            userName={userName}
            accountId={accountId}
            selectedFilterColumn={selectedFilterColumn}
            filterColumns={filterColumns}
            startTime={startTime}
            endTime={endTime}
            onFetchData={onFetchDataByFilter}
          />
        )}

        <DataGrid
          resourceTypes={resourceTypes}
          data={usageInfoList}
          serverPaging
          rowsPerPage={pageSize}
          defaultSorted={sortedByTimestamp}
          page={pageNumber}
          columns={gridColumns}
          efficientPaging
          loading={isPending}
          currentPageComponent={() => pageNumber + 1}
          pageText={'Page'}
          onNextPage={onNextPage}
          onPrevPage={onPrevPage}
          canNext={canNext}
          canPrev={pageNumber > 0}
          onFetchData={onFetchDataByGrid}
        />
      </Tile>
      <CloudTrailLogDetails data={logDetails} onClose={onCloseLogDetails} />
    </>
  )
})

UserActivityAlert.defaultProps = {
  useSearch: true,
  useFilter: true,
  showToastMessages: false,
  hideEmpty: false
}

export default UserActivityAlert
